import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Box, Grid } from '@mui/material'
import { Alert } from '@mui/material'

import { ADMIN_ROLES } from '../../../../core/constants'
import { usePermissions } from '../../../../core/hooks'
import { TextInput, SelectInput, ThemeButton, CnaeSelectInput } from '../../../../ui'
import { useCombos } from '../../../combos'
import { CompanyLogo } from '../../../company-logo'
import { COMPANY_FORM_CONFIG, INSTALLER_WORKS_OPTIONS } from '../../constants'
import { requests } from '../../../../core/ajax'
import { feedback } from '../../../../core/feedback'
import { assembleInstallerSecondaryPutPayload } from '../../logics'
import { useCompanyProfileContext } from '../../contexts'
import { CoporateIdentityPanel } from '../corporate-identity-panel'
import { CompanyFinance, CompanyPublicInput } from '../company-public'
import { CompanyWorksChecklist } from '../company-works-checklist'
import { IbanStateIndicator } from './iban-state-indicator.component'
import { IPInstallers } from './ip-installers.component'
import { PrivacyPolicyCheckbox } from './privacy-policy-checkbox.component'
import { CompanyDocuments } from '../company-documents'
import { PublicOfferForm } from '../public-offer-form'
import { CompanyAddressForm } from '../company-address-form'

export const PrimaryCompanyForm = () => {
  const {
    company,
    formState,
    changeFormState,
    savingCompany,
    setFormStateSaved,
    publicOffer,
    error,
    updateInstallerPrimaryCompany,
  } = useCompanyProfileContext()
  const intl = useIntl()
  const combos = useCombos([])
  const global = useSelector((state) => state.global)
  const isAdmin = ADMIN_ROLES[global.role]
  const { isPrescriptor } = usePermissions()
  const navigate = useNavigate()
  const inputProps = { formState, onChange: changeFormState, combos, intl }
  const bloqueo_negocio = formState.bloqueo_negocio
  const isPrescriptorRole = global.role === 'administrador_prescriptor'

  useEffect(() => {
    if (!isPrescriptorRole || !company.id) return
    const firstFilial = (company.empresas_filiales || [])[0]
    if (!firstFilial) return
    requests.getById('/empresa_ca', firstFilial.id).then(({ data: ca }) => {
      if (ca.iban_op) changeFormState({ target: { name: 'iban_op', value: ca.iban_op } })
      if (ca.certificado_iban_op)
        changeFormState({ target: { name: 'certificado_iban_op', value: ca.certificado_iban_op } })
      setFormStateSaved(true)
    })
  }, [company.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const propagateToFiliales = async (snapshot) => {
    const filialIds = (company.empresas_filiales || []).map((f) => f.id)
    if (filialIds.length === 0) return
    const filialResponses = await Promise.all(
      filialIds.map((id) => requests.getById('/empresa_ca', id))
    )
    await Promise.all(
      filialResponses.map(({ data: filial }) =>
        requests.putById(
          '/empresa_ca',
          filial.id,
          assembleInstallerSecondaryPutPayload({
            ...filial,
            responsable_legal: snapshot.responsable_legal || '',
            nif_responsable_legal: snapshot.nif_responsable_legal || '',
            check_resp_sec_info: true,
            iban_op: snapshot.iban_op,
            certificado_iban_op_nuevo: snapshot.certificado_iban_op_nuevo,
          })
        )
      )
    )
  }

  const requiresRgpd = !!formState.captadora
  const hasRgpd = !!formState.formulario_rgpd

  const handleSubmitForm = async (e) => {
    e.preventDefault()
    if (requiresRgpd && !hasRgpd) {
      feedback('error', intl.formatMessage({ id: 'pages.profile.company.rgpdMissing' }))
      return
    }
    if (isPrescriptorRole) {
      const snapshot = {
        responsable_legal: formState.responsable_legal,
        nif_responsable_legal: formState.nif_responsable_legal,
        iban_op: formState.iban_op,
        certificado_iban_op_nuevo: formState.certificado_iban_op_nuevo,
      }
      try {
        await updateInstallerPrimaryCompany()
        await propagateToFiliales(snapshot)
        navigate('/profile/adhesion-op')
      } catch {
        feedback('error', 'Error al guardar los datos de la empresa')
      }
      return
    }
    updateInstallerPrimaryCompany()
  }

  const roleWorksOptions =
    global.role === 'administrador_prescriptor'
      ? INSTALLER_WORKS_OPTIONS.filter((opt) => opt.key !== 'grabadora')
      : INSTALLER_WORKS_OPTIONS

  const isMatrixDataComplete = Boolean(
    company?.nombre &&
    company?.razon_social &&
    (company?.cif || company?.nif) &&
    company?.telefono &&
    company?.email &&
    company?.calle &&
    company?.numero &&
    company?.poblacion &&
    company?.cod_codigo_postal &&
    company?.cc_aa &&
    company.cc_aa.length > 0 &&
    company?.responsable_legal &&
    company?.nif_responsable_legal
  )

  const hasAnyAdhesion = publicOffer?.some((po) => {
    const allCampaigns = [
      ...(po.campañas?.prescriptor || []),
      ...(po.campañas?.instalador || []),
      ...(po.campañas?.cliente || []),
    ]
    return allCampaigns.some((campaña) => campaña.adhesion_id !== null)
  })

  const showCcaaWarning = isPrescriptor && isMatrixDataComplete && !hasAnyAdhesion

  return (
    <form onSubmit={handleSubmitForm}>
      <Grid container spacing={3}>
        {isAdmin ? (
          <Grid size={12}>
            <CompanyWorksChecklist
              {...{
                options: roleWorksOptions,
                formState,
                onChange: changeFormState,
              }}
            />
          </Grid>
        ) : null}
        {(formState.captadora || global.role === 'administrador_prescriptor') &&
          formState.publica === '01' ? (
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: company.grabadora && (isAdmin || (!isAdmin && company.gremio_ip)) ? 6 : 12,
            }}
          >
            <CompanyLogo {...{ model: formState, onChange: changeFormState, disabled: !isAdmin }} />
          </Grid>
        ) : null}
        {/* NEVO-439 - Limitar ACVC post portal único: se elimina el flujo de "agremiarse" (listas
        de asociaciones) en la vista general de la empresa. */}
        {formState.captadora &&
          isAdmin &&
          !bloqueo_negocio &&
          global.role !== 'administrador_prescriptor' ? (
          <>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <CompanyPublicInput readOnly={!isPrescriptor} />
            </Grid>
            {/* <Grid item xs={12} sm={6}>
              <CompanyFinance />
        </Grid> */}
          </>
        ) : null}
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <TextInput {...{ ...COMPANY_FORM_CONFIG(inputProps).NOMBRE, readOnly: !isAdmin }} />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <TextInput {...{ ...COMPANY_FORM_CONFIG(inputProps).RAZON, readOnly: !isAdmin }} />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <TextInput {...{ ...COMPANY_FORM_CONFIG(inputProps).CIF, readOnly: !isAdmin }} />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <TextInput {...{ ...COMPANY_FORM_CONFIG(inputProps).TELEFONO, readOnly: !isAdmin }} />
        </Grid>
        {formState.captadora || global.role === 'administrador_prescriptor' ? (
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <TextInput {...{ ...COMPANY_FORM_CONFIG(inputProps).WEB, readOnly: !isAdmin }} />
          </Grid>
        ) : null}
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <TextInput {...{ ...COMPANY_FORM_CONFIG(inputProps).EMAIL, readOnly: !isAdmin }} />
        </Grid>
        <Grid size={12}>
          <CompanyAddressForm {...{ inputProps, readOnly: !isAdmin }} />
        </Grid>
        {formState.grabadora ? (
          <>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <SelectInput
                {...{ ...COMPANY_FORM_CONFIG(inputProps).CATEGORIA, readOnly: !isAdmin }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextInput {...{ ...COMPANY_FORM_CONFIG(inputProps).IBAN, readOnly: !isAdmin }} />
              {isAdmin ? <IbanStateIndicator {...{ model: company }} /> : null}
            </Grid>
          </>
        ) : null}
        {formState.captadora || global.role === 'administrador_prescriptor' ? (
          <>
            {!isAdmin &&
              formState.publica === '01' &&
              formState.acepta_financiacion === '01' &&
              formState.captadora ? (
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                }}
              >
                <SelectInput
                  {...{ ...COMPANY_FORM_CONFIG(inputProps).ACEPTA_F, readOnly: !isAdmin }}
                />
              </Grid>
            ) : null}
            <Grid
              size={{
                xs: 12,
                sm: 12,
              }}
            >
              <SelectInput {...{ ...COMPANY_FORM_CONFIG(inputProps).CCAA, readOnly: !isAdmin }} />
            </Grid>
            {/* CNAE - Show only for administrador_prescriptor */}
            {global.role === 'administrador_prescriptor' ? (
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                }}
              >
                <CnaeSelectInput
                  {...{ ...COMPANY_FORM_CONFIG(inputProps).CNAE, readOnly: !isAdmin }}
                />
              </Grid>
            ) : null}
            {formState.publica === '01' &&
              (formState.captadora || global.role === 'administrador_prescriptor') ? (
              <Grid size={12}>
                <TextInput
                  {...{ ...COMPANY_FORM_CONFIG(inputProps).DESCRIPCION, readOnly: !isAdmin }}
                />
              </Grid>
            ) : null}
          </>
        ) : null}
        {formState.captadora || isPrescriptorRole ? (
          <Grid size={12}>
            <PublicOfferForm {...{ isPrimaryCompany: true }} />
          </Grid>
        ) : null}
        <Grid size={12}>
          <CompanyDocuments {...{ isPrimaryCompany: true }} />
        </Grid>
        {company.grabadora &&
          formState.grabadora &&
          formState.grabadora &&
          isAdmin &&
          global.role !== 'administrador_prescriptor' ? (
          <Grid size={12}>
            <IPInstallers {...{ company: formState }} />
          </Grid>
        ) : null}
        {formState.captadora || global.role === 'administrador_prescriptor' ? (
          <Grid size={12}>
            <PrivacyPolicyCheckbox company={company} />
          </Grid>
        ) : null}
        {formState.captadora || global.role === 'administrador_prescriptor' ? (
          <Grid size={12}>
            <CoporateIdentityPanel {...{ disabled: !isAdmin }} />
          </Grid>
        ) : null}
      </Grid>
      {error.message ? (
        <Box mt={2} mb={1}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      ) : null}

      {showCcaaWarning && (
        <Box my={2}>
          <Alert severity="warning">
            {intl.formatMessage({ id: 'pages.profile.company.feedback.cca.autoadhesion.warning' })}
          </Alert>
        </Box>
      )}

      {isAdmin ? (
        <Box display="flex" justifyContent="center" width="100%" mt={4}>
          <ThemeButton
            color="primary"
            type="submit"
            disabled={savingCompany}
            loading={savingCompany}
          >
            {intl.formatMessage({ id: 'pages.profile.company.button.save' })}
          </ThemeButton>
        </Box>
      ) : null}
    </form>
  )
}
